import React from "react";
import RequestEngine from "../../core/RequestEngine";
import strings from "../../core/translate";
import EmptyState from "../../components/Page/EmptyState";
import { apiErrorMessage } from "../../shared/api-error";
import "../../assets/css/appointment-detail.css";

/**
 * Who is coming, in what car, and what was done to that car before — the same
 * three questions the worker app answers from a job card.
 */
export default class AppointmentDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      bookings: [],
      selectedBookingId: "",
      history: [],
      historyLoading: false,
      acting: "",
      error: "",
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    this.loadBookings();
  }

  fetchList = async (path) => {
    try {
      const response = await this.engine.getItem(path);
      if (!response || response.status !== 200) return [];
      return Array.isArray(response.data)
        ? response.data
        : response.data.items || [];
    } catch (err) {
      return [];
    }
  };

  loadBookings = async (keepId) => {
    const { appointmentId } = this.props;
    const bookings = await this.fetchList(
      `merchants-branches/${appointmentId}/appointment-booking`
    );
    this.setState({ loading: false, bookings });
    if (bookings.length > 0) {
      const keep = bookings.find((item) => item.id === keepId);
      this.selectBooking(keep || bookings[0]);
    }
  };

  selectBooking = async (booking) => {
    const vehicle = booking.customerVehicle;
    this.setState({ selectedBookingId: booking.id, history: [] });
    if (!vehicle || !vehicle.id) return;

    this.setState({ historyLoading: true });
    const history = await this.fetchList(
      `merchants-branches/${this.props.branchId}/work-orders/by-vehicle/${vehicle.id}`
    );
    this.setState({
      // the visit being looked at is not part of its own history
      history: history.filter((row) => row.bookingId !== booking.id),
      historyLoading: false,
    });
  };

  act = async (booking, action) => {
    if (action === "reject") {
      const reason = window.prompt(strings.reject_reason_prompt);
      if (!reason) return;
      await this.send(booking, "reject", { statusReason: reason });
      return;
    }
    // Starting service is receiving the car, so where the caller can take the
    // garage to a job card it hands over instead of flipping the status on its
    // own — opening the card advances the booking anyway. Callers that cannot
    // navigate keep the plain status change.
    if (action === "start" && this.props.onStartService) {
      this.props.onStartService(booking);
      return;
    }
    await this.send(booking, action);
  };

  send = async (booking, action, body) => {
    this.setState({ acting: action, error: "" });
    try {
      await this.engine.patchItem(
        `merchants-branches/${this.props.branchId}/bookings/${booking.id}/${action}`,
        body || {}
      );
      await this.loadBookings(booking.id);
      if (this.props.onChanged) this.props.onChanged();
    } catch (err) {
      this.setState({ error: apiErrorMessage(err) });
    }
    this.setState({ acting: "" });
  };

  /** What the garage may do next, exactly as the worker app decides it. */
  actionsFor(status) {
    switch (`${status}`.toUpperCase()) {
      case "PENDING":
        return ["accept", "reject"];
      case "ACCEPTED":
        return ["start", "reject"];
      case "STARTED":
        return ["finish"];
      default:
        return [];
    }
  }

  renderStatus(booking) {
    const actions = this.actionsFor(booking.status);
    const { acting } = this.state;

    return (
      <div className="detail-card detail-status">
        <div className="detail-status__head">
          <span className={`booking-status is-${booking.status}`}>
            {strings[`booking_${`${booking.status}`.toLowerCase()}`] ||
              booking.status}
          </span>
          {booking.statusReason && (
            <span className="detail-status__reason">{booking.statusReason}</span>
          )}
        </div>

        {actions.length > 0 && (
          <div className="detail-status__actions">
            {actions.map((action) => (
              <button
                type="button"
                key={action}
                disabled={Boolean(acting)}
                className={`booking-action is-${action}`}
                onClick={() => this.act(booking, action)}
              >
                {action === "start" && this.props.onStartService
                  ? strings.start_service
                  : strings[`booking_action_${action}`] || action}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderCustomer(booking) {
    const customer = booking.customer || {};
    const user = customer.user || customer;
    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();

    return (
      <div className="detail-card">
        <h6 className="detail-card__title">
          <i className="nc-icon nc-single-02" />
          {strings.customer}
        </h6>
        <dl className="detail-list">
          <dt>{strings.name}</dt>
          <dd>{name || "—"}</dd>
          <dt>{strings.phone}</dt>
          <dd>{user.phoneNumber || "—"}</dd>
          <dt>{strings.email}</dt>
          <dd>{user.email || "—"}</dd>
        </dl>
      </div>
    );
  }

  renderVehicle(booking) {
    const vehicle = booking.customerVehicle || {};
    const label = (value) => (value && value.name) || value || "—";

    return (
      <div className="detail-card">
        <h6 className="detail-card__title">
          <i className="nc-icon nc-delivery-fast" />
          {strings.vehicle}
        </h6>
        <dl className="detail-list">
          <dt>{strings.platenumber}</dt>
          <dd className="is-strong">
            {[vehicle.plateCode, vehicle.plateNumber].filter(Boolean).join(" ") ||
              "—"}
          </dd>
          <dt>{strings.make}</dt>
          <dd>{label(vehicle.make)}</dd>
          <dt>{strings.model}</dt>
          <dd>{label(vehicle.model)}</dd>
          <dt>{strings.year}</dt>
          <dd>{vehicle.year || "—"}</dd>
          <dt>{strings.vin}</dt>
          <dd>{vehicle.vin || "—"}</dd>
        </dl>
      </div>
    );
  }

  renderHistory() {
    const { history, historyLoading } = this.state;

    if (historyLoading) {
      return <p className="detail-muted">{strings.loading}…</p>;
    }
    if (history.length === 0) {
      return (
        <EmptyState
          icon="nc-icon nc-settings-tool-66"
          title={strings.no_previous_work_orders}
        />
      );
    }

    const { onOpenWorkOrder } = this.props;

    return (
      <ul className="detail-history">
        {history.map((row) => (
          <li
            key={row.id}
            className={onOpenWorkOrder ? "is-clickable" : ""}
            role={onOpenWorkOrder ? "button" : undefined}
            tabIndex={onOpenWorkOrder ? 0 : undefined}
            onClick={onOpenWorkOrder ? () => onOpenWorkOrder(row) : undefined}
            onKeyDown={
              onOpenWorkOrder
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenWorkOrder(row);
                    }
                  }
                : undefined
            }
          >
            <span className="detail-history__date">
              {`${row.date || ""}`.slice(0, 10)}
            </span>
            <span className="detail-history__issues">
              {row.issues && row.issues.length > 0
                ? row.issues.slice(0, 3).join(", ")
                : `${row.itemsCount || 0} ${strings.items}`}
            </span>
            <span className={`work-order-status is-${row.status}`}>
              {strings[row.status] || row.status}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  render() {
    const { loading, bookings, selectedBookingId } = this.state;

    if (loading) {
      return <p className="detail-muted">{strings.loading}…</p>;
    }

    if (bookings.length === 0) {
      return (
        <EmptyState
          icon="nc-icon nc-calendar-60"
          title={strings.no_bookings}
          hint={strings.no_bookings_hint}
        />
      );
    }

    const booking =
      bookings.find((item) => item.id === selectedBookingId) || bookings[0];

    return (
      <div className="appointment-detail">
        {bookings.length > 1 && (
          <div className="detail-tabs">
            {bookings.map((item) => {
              const vehicle = item.customerVehicle || {};
              return (
                <button
                  type="button"
                  key={item.id}
                  className={`detail-tab${
                    item.id === booking.id ? " is-active" : ""
                  }`}
                  onClick={() => this.selectBooking(item)}
                >
                  {vehicle.plateNumber || strings.vehicle}
                </button>
              );
            })}
          </div>
        )}

        {this.renderStatus(booking)}
        {this.state.error && (
          <div className="mform__alert">
            <i className="nc-icon nc-alert-circle-i" />
            <span>{this.state.error}</span>
          </div>
        )}

        <div className="detail-grid">
          {this.renderCustomer(booking)}
          {this.renderVehicle(booking)}
        </div>

        <div className="detail-card">
          <h6 className="detail-card__title">
            <i className="nc-icon nc-settings-tool-66" />
            {strings.previous_work_orders}
          </h6>
          {this.renderHistory()}
        </div>
      </div>
    );
  }
}
